import { API } from "@/lib/api";

/**
 * POST a JSON body to a streaming SSE endpoint and yield token deltas.
 *
 * The Imali backend streams three event types:
 *   - `meta`   : one initial event with proposal metadata
 *   - `token`  : one per streamed chunk (`{content: string}`)
 *   - `error`  : optional, on failure (`{detail: string}`)
 *   - `done`   : final event with `{content: string}` (full text)
 *
 * @param {string} path        Path like `/generate/loan-proposal/stream`
 * @param {object} body        JSON body to send
 * @param {object} handlers    { onMeta, onToken, onDone, onError }
 * @param {AbortSignal} signal Optional abort signal
 */
export async function streamProposal(path, body, handlers = {}, signal) {
  const { onMeta, onToken, onDone, onError } = handlers;

  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Stream failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const parseEvent = (raw) => {
    // raw is a block delimited by "\n\n"
    let event = "message";
    const dataLines = [];
    for (const line of raw.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
    }
    const data = dataLines.join("\n");
    return { event, data };
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      if (!raw.trim()) continue;
      const { event, data } = parseEvent(raw);
      let payload = null;
      try {
        payload = data ? JSON.parse(data) : null;
      } catch {
        continue;
      }

      if (event === "meta") onMeta?.(payload);
      else if (event === "token") onToken?.(payload?.content || "");
      else if (event === "done") {
        onDone?.(payload?.content || "");
        return;
      } else if (event === "error") {
        onError?.(payload?.detail || "stream error");
        throw new Error(payload?.detail || "stream error");
      }
    }
  }
}
